import React, {FC} from 'react'
import {Box, Button, CircularProgress} from '@material-ui/core'
import {flowMax, addDisplayName, addHandlers} from 'ad-hok'

import Dialog from 'components/Dialog'
import DialogTitle from 'components/DialogTitle'
import DialogContent from 'components/DialogContent'
import DialogActions from 'components/DialogActions'
import TextField from 'components/TextField'
import PasswordField from 'components/PasswordField'
import Form from 'components/Form'
import {staticMedicaidLoginFormSchema} from './schema'
import {addTranslationHelpers} from 'utils/i18n'

interface MedicaidApplicationLoginDialogProps {
  open: boolean
  onSubmit: (credentials: {email: string; password: string}) => void
  onClose: () => void
  loginStatus: {
    inProgress: boolean
    error: boolean
    initialValues: {credentials: {email: string; password: string}}
  }
}

const MedicaidApplicationLoginDialog: FC<MedicaidApplicationLoginDialogProps> = flowMax(
  addDisplayName('MedicaidApplicationLoginDialog'),
  addHandlers({
    handleClose: ({onClose}) => (
      event: React.SyntheticEvent,
      reason: 'backdropClick' | 'escapeKeyDown'
    ) => {
      if (reason === 'backdropClick') return
      onClose()
    },
  }),
  addTranslationHelpers,
  ({open, onSubmit, handleClose, onClose, loginStatus, t}) => (
    <Dialog open={open} onClose={handleClose}>
      <Form
        name={'medicaidApplicationLoginForm'}
        schema={staticMedicaidLoginFormSchema}
        onSubmitSuccess={(values) =>
          !loginStatus.inProgress && onSubmit(values.formValues.credentials)
        }
        initialValues={loginStatus.initialValues}
        shouldResetFormOnInitialValuesChange={true}
      >
        <DialogTitle>{t('medicaidApplicationLoginForm.title')}</DialogTitle>
        <DialogContent>
          <p>{t('medicaidApplicationLoginForm.description')}</p>
          <TextField name="credentials.email" />
          <PasswordField name="credentials.password" />
          {loginStatus.error && (
            <p style={{color: 'red'}}>
              {t('medicaidApplicationLoginForm.loginValidationError')}
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={onClose}
            color="secondary"
            disabled={loginStatus.inProgress}
          >
            Cancel
          </Button>
          <Button type="submit" color="primary">
            <Box display="flex" alignItems="center" gridGap={5}>
              {loginStatus.inProgress
                ? t('medicaidApplicationLoginForm.button.loggingIn')
                : t('medicaidApplicationLoginForm.button.login')}
              {loginStatus.inProgress && <CircularProgress size={12} />}
            </Box>
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
)

export default MedicaidApplicationLoginDialog
